import type { LeverageStatus, LeverageType, Receipt, ReceiptSource, ReceiptType, TransferStatus } from './types';

/**
 * Os tipos nos filtros do painel e do ranking. O gás não entra (não é da
 * operação: tem o próprio bloco no painel), nem o depósito sem alavancagem —
 * ele fica na aba Depósitos dos comprovantes.
 */
export const RECEIPT_TYPES: LeverageType[] = ['WELCOME', 'EVENT', 'SALE', 'LIVE'];

/**
 * Os tipos, na ordem do formulário: os dois depósitos com alavancagem, o
 * depósito na UNA Markets, gás, venda, repasse live e, por último, o depósito
 * sem alavancagem.
 */
export const RECEIPT_FORM_TYPES: ReceiptType[] = ['WELCOME', 'EVENT', 'UNA_DEPOSIT', 'GAS', 'SALE', 'LIVE', 'DEPOSIT'];

/** As vendas em cripto são uma aba dos comprovantes: o endereço que abre direto nela. */
export const CRYPTO_TAB_PATH = '/comprovantes?aba=cripto';

/** As situações da alavancagem, na ordem do formulário. */
export const LEVERAGE_STATUSES: LeverageStatus[] = ['PENDING', 'DONE', 'FAILED'];

/** Só a venda pode ficar sem cliente (o servidor confere — assertLeadRule). */
export const requiresLead = (type: ReceiptType | '' | null): boolean => type !== '' && type !== null && type !== 'SALE';

/** Senha escrita na observação — a mesma regra do servidor (receipt.rules.ts). */
export const hasPasswordInNote = (note: string): boolean => /senha|password|\b(pass|pwd|psw)\b/i.test(note);

/** As situações do repasse, na ordem do formulário. */
export const TRANSFER_STATUSES: TransferStatus[] = ['NONE', 'PENDING', 'DONE'];

/** O gás: o cliente pondo dinheiro de novo na conta. Fora do vendido e do salário. */
export const isGas = (type: ReceiptType | '' | null): boolean => type === 'GAS';

/** O depósito sem alavancagem: o valor é o depositado, fora do vendido. */
export const isDeposit = (type: ReceiptType | '' | null): boolean => type === 'DEPOSIT';

/** O depósito na UNA Markets: corretora à parte, fora do depositado da outra e do vendido. */
export const isUnaDeposit = (type: ReceiptType | '' | null): boolean => type === 'UNA_DEPOSIT';

/**
 * O depósito com alavancagem (boas-vindas ou evento): nasce com o depositado, e
 * a alavancagem buscada (`soldAmount`) só entra quando ela é realizada.
 */
export const isAlavancagem = (type: ReceiptType | '' | null): boolean => type === 'WELCOME' || type === 'EVENT';

/** A alavancagem buscada, quando já existe: só a realizada tem. `null` = ainda não. */
export const soughtOf = (receipt: Pick<Receipt, 'leverageStatus' | 'soldAmount'>): number | null =>
  receipt.leverageStatus === 'DONE' && receipt.soldAmount > 0 ? receipt.soldAmount : null;

/**
 * O comprovante de repasse (o código guarda o nome histórico, LIVE). Ele é só
 * o repasse: pendente ou feito e, feito, quanto foi puxado. Não tem produto,
 * valor vendido nem alavancagem, e fica fora do vendido.
 */
export const isTransfer = (type: ReceiptType | '' | null): boolean => type === 'LIVE';

/** As situações do comprovante de repasse: ele sempre tem repasse, então não há "sem repasse". */
export const TRANSFER_RECEIPT_STATUSES: TransferStatus[] = ['PENDING', 'DONE'];

/** Repasse de um comprovante novo, enquanto ninguém escolhe: venda, depósitos e gás não têm; alavancagem, pendente. */
export const defaultTransferStatus = (type: ReceiptType | ''): TransferStatus =>
  type === 'SALE' || type === 'DEPOSIT' || type === 'UNA_DEPOSIT' || type === 'GAS' ? 'NONE' : 'PENDING';

/** Origem do comprovante, nos filtros: a compra paga no checkout entra sozinha; o comprovante, anexado. */
export const RECEIPT_SOURCE_LABELS: Record<ReceiptSource, string> = {
  EPHRA: 'Vendas do checkout (Ephra)',
  CAKTO: 'Vendas do checkout (Cakto)',
  KIRVANO: 'Vendas do checkout (Kirvano)',
  DOTPAG: 'Vendas do checkout (DotPag)',
  MUNDPAY: 'Vendas do checkout (MundPay)',
  MANUAL: 'Comprovantes anexados',
  CRYPTO: 'Parcelas em cripto',
};

/**
 * A situação da venda anexada na conferência: em análise, conferida no checkout,
 * aprovada sem checkout, comprovada pelo admin, reembolsada, reprovada.
 */
export type ReviewView = 'PENDING' | 'LINKED' | 'APPROVED' | 'CONFIRMED' | 'REFUNDED' | 'REJECTED';

export const reviewViewOf = (receipt: Pick<Receipt, 'reviewStatus' | 'orderKey' | 'adminConfirmed'>): ReviewView =>
  receipt.reviewStatus === 'APPROVED'
    ? receipt.adminConfirmed ? 'CONFIRMED' : receipt.orderKey ? 'LINKED' : 'APPROVED'
    : receipt.reviewStatus;

export const REVIEW_LABELS: Record<ReviewView, string> = {
  PENDING: 'Em análise',
  LINKED: 'Conferida no checkout',
  APPROVED: 'Aprovada sem checkout',
  CONFIRMED: 'Comprovada pelo admin',
  REFUNDED: 'Reembolsada',
  REJECTED: 'Reprovada',
};

export const REVIEW_CHIP: Record<ReviewView, string> = {
  PENDING: 'chip chip-warning',
  LINKED: 'chip chip-positive',
  APPROVED: 'chip chip-positive',
  CONFIRMED: 'chip chip-positive',
  REFUNDED: 'chip chip-refunded',
  REJECTED: 'chip chip-negative',
};

export const REVIEW_HINTS: Record<ReviewView, string> = {
  PENDING: 'Não conta no vendido: o comprovante é a prova da venda. O administrador confere com a compra no checkout; a venda conta pela compra.',
  LINKED: 'A compra paga no checkout bate com este comprovante. A venda conta pela compra, pelo preço sem juros.',
  APPROVED: 'Sem compra no checkout (Pix direto, transferência), aprovada pelo administrador. Conta no vendido só a venda com data até 26/10/2026.',
  CONFIRMED: 'O administrador comprovou esta venda: ela conta no vendido e na nota do ranking, por decisão dele.',
  REFUNDED: 'Fora do vendido: a compra no checkout foi reembolsada ao cliente.',
  REJECTED: 'Fora do vendido: o administrador reprovou. Corrija o comprovante para ele ser conferido de novo.',
};

/** A forma curta da plataforma da compra vinculada: "CAKTO:abc" → "Cakto". */
export const platformOfKey = (key: string | null): string | null => (key ? PLATFORM_LABELS[key.slice(0, key.indexOf(':'))] ?? null : null);

/** Só a venda anexada passa pela conferência — a parcela do carnê e os outros tipos não. */
export const isReviewed = (receipt: Pick<Receipt, 'source' | 'leverageType'>): boolean =>
  receipt.source === 'MANUAL' && countsAsSold(receipt.leverageType);

/** A plataforma da compra no checkout, por extenso. */
export const PLATFORM_LABELS: Record<string, string> = { CAKTO: 'Cakto', KIRVANO: 'Kirvano', DOTPAG: 'DotPag', MUNDPAY: 'MundPay', EPHRA: 'Ephra' };

/**
 * Só a venda é vendido — e o comprovante antigo, sem tipo (`null`). Depósito,
 * alavancagem, repasse e gás têm o próprio valor, fora de todo total vendido
 * (a conta é do servidor — sold.ts). `''` é o formulário antes de escolher.
 */
export const countsAsSold = (leverageType: ReceiptType | '' | null): boolean =>
  leverageType === 'SALE' || leverageType === '' || leverageType === null;

/** Tem valor próprio fora do vendido: depósitos, alavancagem e gás (o repasse mostra o puxado). */
export const hasOwnValue = (leverageType: ReceiptType | '' | null): boolean =>
  isDeposit(leverageType) || isUnaDeposit(leverageType) || isAlavancagem(leverageType) || isGas(leverageType);

/** O valor próprio na lista: na alavancagem, o depositado (a buscada tem coluna própria); nos outros, o valor. */
export const ownValueOf = (receipt: Pick<Receipt, 'leverageType' | 'soldAmount' | 'depositAmount'>): number =>
  isAlavancagem(receipt.leverageType) ? receipt.depositAmount : receipt.soldAmount;

/** Explica, ao passar o mouse, por que aquele valor não soma no vendido. */
export const notSoldHint = (leverageType: ReceiptType | '' | null): string =>
  isGas(leverageType)
    ? 'Gás: fica fora do vendido e do salário — aparece no bloco do gás no painel'
    : isUnaDeposit(leverageType)
      ? 'Depósito UNA Markets: valor depositado na UNA, fora do vendido e do depositado da outra corretora'
      : isDeposit(leverageType)
        ? 'Depósito: valor depositado, fora do vendido'
        : isAlavancagem(leverageType)
          ? 'Depósito com alavancagem: valor depositado, fora do vendido'
          : 'Repasse: não entra no valor vendido';
